import React, { useState } from 'react'
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import {
  useGetDailyStocksQuery,
  useDeleteDailyStockMutation
} from '../../slices/dailyStocksApiSlice'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

const DailyStockListScreen = () => {
  // ─── State ────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState('')
  const [refineFilter, setRefineFilter] = useState('') // second filter
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // ─── Data Fetching ─────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    error,
    refetch
  } = useGetDailyStocksQuery(pageNumber)
  const [deleteDailyStock, { isLoading: loadingDelete }] =
    useDeleteDailyStockMutation()

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this dailyStock?')) return
    try {
      await deleteDailyStock(id).unwrap()
      toast.success('DailyStock deleted!')
      refetch()
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete')
    }
  }

  const paginationHandler = (page) => setPageNumber(page)

  // ─── Filtering Logic (ORIGINAL, unchanged) ────────────────────────────────
  const filterDailyStocks = () => {
    if (!data?.dailyStocks) return []
    return data.dailyStocks.filter((dailyStock) => {
      const dailyStockDate = new Date(dailyStock.date)
      const inRange =
        (!startDate || dailyStockDate >= new Date(startDate)) &&
        (!endDate || dailyStockDate <= new Date(endDate))

      const noteMatch = dailyStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemMatch = dailyStock.dailyStockItems.some((i) =>
        i.name.toLowerCase().includes(filter.toLowerCase())
      )

      return inRange && (noteMatch || itemMatch)
    })
  }

  // ─── Total Value (refine on items with second filter) ─────────────────────
  const calculateTotalDailyStocks = () => {
    return filterDailyStocks().reduce((acc, dailyStock) => {
      const noteMatch = dailyStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirstFilter = noteMatch
        ? dailyStock.dailyStockItems
        : dailyStock.dailyStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecondFilter = itemsAfterFirstFilter.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return (
        acc +
        itemsAfterSecondFilter.reduce(
          (sum, i) => sum + i.qty * i.price,
          0
        )
      )
    }, 0)
  }

  // ─── Excel Export (refine on items with second filter) ────────────────────
  const exportToExcel = () => {
    const rows = filterDailyStocks().flatMap((dailyStock) => {
      const noteMatch = dailyStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirstFilter = noteMatch
        ? dailyStock.dailyStockItems
        : dailyStock.dailyStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecondFilter = itemsAfterFirstFilter.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return itemsAfterSecondFilter.map((i) => ({
        Date: new Date(dailyStock.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: dailyStock.note || 'N/A',
        User: dailyStock.username || 'N/A'
      }))
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'DailyStocks')
    XLSX.writeFile(wb, 'DailyStocks_Report.xlsx')
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>DailyStocks</h1>
          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>
          <Link to='/admin/dailystocksearchReplace' className='btn btn-light my-3'>
          Search
          </Link>
          <Link to='/admin/dailystockdelete' className='btn btn-light my-3'>
          Del
          </Link>
          <Link to='/admin/dailystocknotesearchReplace' className='btn btn-light my-3'>
          NoteSearch
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product name"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total DailyStocks: ${calculateTotalDailyStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading dailyStocks'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>USER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterDailyStocks().map((dailyStock) => {
                const noteMatch = dailyStock.note
                  ?.toLowerCase()
                  .includes(filter.toLowerCase())

                const itemsAfterFirstFilter = noteMatch
                  ? dailyStock.dailyStockItems
                  : dailyStock.dailyStockItems.filter((i) =>
                      i.name.toLowerCase().includes(filter.toLowerCase())
                    )

                const itemsAfterSecondFilter = itemsAfterFirstFilter.filter(
                  (i) =>
                    i.name
                      .toLowerCase()
                      .includes(refineFilter.toLowerCase())
                )

                const sorted = [...itemsAfterSecondFilter].sort((a, b) =>
                  a.name.localeCompare(b.name)
                )

                return sorted.map((item) => (
                  <tr key={`${dailyStock._id}-${item.name}`}>
                    <td>
                      {new Date(dailyStock.date).toLocaleDateString()}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>{dailyStock.note || 'N/A'}</td>
                    <td>{dailyStock.username || 'N/A'}</td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="thumb"
                          className="thumbnail-img me-2"
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '5px'
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/dailyStock/${dailyStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        as={Link}
                        to={`/admin/dailyStock/${dailyStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                          as={Link}
                          to={`/admincopy/dailyStock/${dailyStock._id}/copy`}
                          variant="light"
                          className="btn-sm mx-2"
                     >
                       Copy
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(dailyStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default DailyStockListScreen
